/**
 * HeadingsSection — PRP-MCP-15 (F1, F2): the user's estimate headings, one per company
 * they write for. Each holds the company heading, logo, opening and closing statements
 * and estimator contact. Fill one by uploading that company's own Xactimate export, or
 * type it. None applies unless one is marked the default; an estimate chooses its own on
 * the estimate page (EstimateHeadingPicker). Reads/writes /me/headings (headingsApi.ts).
 * The page's own `Section` frame is passed in, like PlanCreditsSection.
 *
 * Only changed fields are sent, so an imported statement's formatting (bold, line
 * spacing) survives a save that did not touch it.
 */
import { useEffect, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { HeadingsAPI } from '../../lib/headingsApi';
import type { CompanyEstimator, Heading, HeadingUpdate } from '../../lib/headingsApi';
import type { HeadingStyle } from '../../types/headingStyle';
import { headingList } from '../../lib/headingsShape';
import HeadingStyleControls from './HeadingStyleControls';
import HeadingsBoundary from './HeadingsBoundary';
import FloodClaimsSection from './FloodClaimsSection';
import NoteLevelSection from './NoteLevelSection';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Pill } from './ui/pill';
import { Textarea } from './ui/textarea';

interface FrameProps {
  title: string;
  sub?: string;
  children: ReactNode;
  footer?: ReactNode;
}

type TextKey = 'name' | 'company_name' | 'company_code' | 'header_info' | 'opening_statement' | 'closing_statement';
export const ESTIMATOR_FIELDS: Array<[keyof CompanyEstimator, string]> = [
  ['name', 'Estimator name'],
  ['adj_no', 'Adjuster number'],
  ['phone', 'Phone'],
  ['email', 'Email'],
];
const NEW = 'new';

export function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block mb-[6px] text-[11px] font-[560] tracking-[.02em] text-[var(--tx-lo)]">
      {children}
    </label>
  );
}

export function errorText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

const LOAD_FAILED = 'Could not load your headings. Try again later.';
const TITLE = 'Estimate headings';

/** A render failure inside the section shows the load-failed line in its frame, never a blank page. */
export default function HeadingsSection({ frame: Frame }: { frame: ComponentType<FrameProps> }) {
  return (
    <>
    <HeadingsBoundary fallback={
      <Frame title={TITLE}>
        <p className="m-0 text-[12.5px] text-[var(--bad)]" data-testid="heading-flash">{LOAD_FAILED}</p>
      </Frame>
    }>
      <HeadingsSectionBody frame={Frame} />
    </HeadingsBoundary>
    {/* PRP-FLOOD-1 (FL2): outside the headings boundary so one section's failure cannot blank the other. */}
    <div className="mt-4"><FloodClaimsSection frame={Frame} /></div>
    {/* PRP-NOTES-1: the account default for chat's suggested customer notes. */}
    <div className="mt-4"><NoteLevelSection frame={Frame} /></div>
    </>
  );
}

function HeadingsSectionBody({ frame: Frame }: { frame: ComponentType<FrameProps> }) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [limit, setLimit] = useState(25);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);   // never claim "no headings yet" after a failed read
  const [editing, setEditing] = useState<string | null>(null);   // a heading id, NEW, or null
  const [draft, setDraft] = useState<Partial<Record<TextKey, string>>>({});
  const [estDraft, setEstDraft] = useState<CompanyEstimator>({});
  const [styleDraft, setStyleDraft] = useState<HeadingStyle | null | undefined>(undefined);   // undefined = untouched
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'bad'; msg: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const importInto = useRef<string | undefined>(undefined);

  async function reload() {
    const d = headingList(await HeadingsAPI.list());
    setHeadings(d.headings);
    setLoadFailed(false);
    setLimit(d.limit);
  }

  useEffect(() => {
    let cancelled = false;
    HeadingsAPI.list()
      .then((raw) => {
        if (cancelled) return;
        const d = headingList(raw);
        setHeadings(d.headings);
        setLimit(d.limit);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          setFlash({ kind: 'bad', msg: LOAD_FAILED });
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const current = headings.find((h) => h.id === editing) ?? null;
  const value = (k: TextKey) => draft[k] ?? (current?.[k] as string | null | undefined) ?? '';
  const estValue = (k: keyof CompanyEstimator) => estDraft[k] ?? current?.estimator?.[k] ?? '';
  const dirty = Object.keys(draft).length > 0 || Object.keys(estDraft).length > 0 || styleDraft !== undefined;
  const hasDefault = headings.some((h) => h.is_default);

  function edit(id: string | null) {
    setEditing(id);
    setDraft({});
    setEstDraft({});
    setStyleDraft(undefined);
    setFlash(null);
  }

  async function run(key: string, work: () => Promise<string>, failMsg: string) {
    setBusy(key);
    setFlash(null);
    try {
      const msg = await work();
      setFlash({ kind: 'ok', msg });
    } catch (e) {
      setFlash({ kind: 'bad', msg: errorText(e, failMsg) });
    } finally {
      setBusy(null);
    }
  }

  function pickFile(into?: string) {
    importInto.current = into;
    fileRef.current?.click();
  }

  async function onFile(file: File | undefined) {
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    await run('import', async () => {
      const out = await HeadingsAPI.importEsx(file, importInto.current);
      await reload();
      edit(out.heading.id);
      return `Read from ${file.name} into "${out.heading.name}".`;
    }, 'Could not read that file.');
  }

  async function onSave() {
    const body: HeadingUpdate = { ...draft };
    if (Object.keys(estDraft).length > 0) body.estimator = { ...(current?.estimator ?? {}), ...estDraft };
    if (styleDraft !== undefined) body.heading_style = styleDraft;
    await run('save', async () => {
      const out = editing === NEW ? await HeadingsAPI.create(body) : await HeadingsAPI.update(editing as string, body);
      await reload();
      edit(out.heading.id);
      return 'Saved.';
    }, 'Could not save.');
  }

  async function onDefault(h: Heading) {
    await run(`default-${h.id}`, async () => {
      await HeadingsAPI.setDefault(h.is_default ? null : h.id);
      await reload();
      return h.is_default
        ? 'No default now. An estimate exports with no heading unless you choose one on it.'
        : `"${h.name}" is now the default for estimates with no heading chosen.`;
    }, 'Could not change the default.');
  }

  async function onDelete(h: Heading) {
    if (!window.confirm(`Delete the heading "${h.name}"? Estimates that chose it will export with no heading.`)) return;
    await run(`delete-${h.id}`, async () => {
      await HeadingsAPI.remove(h.id);
      await reload();
      if (editing === h.id) edit(null);
      return `Deleted "${h.name}".`;
    }, 'Could not delete.');
  }

  const text = (k: TextKey, label: string, rows?: number) => {
    const id = `heading-${k}`;
    return (
      <div>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        {rows ? (
          <Textarea id={id} rows={rows} value={value(k)} data-testid={id}
            onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
        ) : (
          <Input id={id} value={value(k)} data-testid={id}
            onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
        )}
      </div>
    );
  };

  return (
    <Frame
      title={TITLE}
      sub="Letterhead, logo, statements and estimator for each company you write for. Choose one on each estimate."
      footer={
        editing && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => edit(null)} disabled={busy !== null}>Close</Button>
            <Button variant="primary" onClick={onSave} disabled={!dirty || busy !== null} data-testid="heading-save">
              {busy === 'save' ? 'Saving…' : editing === NEW ? 'Add heading' : 'Save heading'}
            </Button>
          </div>
        )
      }
    >
      {/* Settings is wide (tabs, 2026-10-28): the list sits left and the open editor right at >=1024px. */}
      <div data-testid="headings"
        className={editing ? 'grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start' : 'space-y-4'}>
        <div className="space-y-4 min-w-0">
          <input ref={fileRef} type="file" accept=".esx,.ESX" className="hidden" data-testid="heading-file"
            onChange={(e) => onFile(e.target.files?.[0])} />
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" disabled={busy !== null || headings.length >= limit} onClick={() => pickFile()}
              data-testid="heading-import">
              {busy === 'import' ? 'Reading…' : 'Copy a heading from an ESX'}
            </Button>
            <Button variant="secondary" disabled={busy !== null || headings.length >= limit} onClick={() => edit(NEW)}
              data-testid="heading-add">
              Type a new heading
            </Button>
            <span className="text-[11.5px] text-[var(--tx-faint)]">{headings.length} of {limit}</span>
          </div>
          {loaded && !loadFailed && headings.length === 0 && (
            <p className="m-0 text-[12.5px] text-[var(--tx-lo)]" data-testid="headings-empty">
              No headings yet. Exports carry no company heading until you add one and choose it on an estimate.
            </p>
          )}
          {headings.length > 0 && !hasDefault && (
            <p className="m-0 text-[12.5px] text-[var(--tx-lo)]" data-testid="headings-no-default">
              No default: an estimate exports with no heading unless you choose one on it.
            </p>
          )}
          {headings.length > 0 && (
            <ul className="m-0 p-0 list-none divide-y divide-[var(--bd)] border border-[var(--bd)] rounded-[var(--r-md)]">
              {headings.map((h) => (
                <li key={h.id} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`heading-row-${h.id}`}>
                  {h.logo && <img src={h.logo} alt="" className="h-6 w-auto rounded-[3px]" />}
                  <span className="text-[13px] text-[var(--tx-hi)] font-[560]">{h.name}</span>
                  {h.company_name && h.company_name !== h.name && (
                    <span className="text-[12px] text-[var(--tx-lo)]">{h.company_name}</span>
                  )}
                  {h.is_default && <span data-testid={`heading-default-${h.id}`}><Pill kind="accent">Default</Pill></span>}
                  <span className="ml-auto flex gap-1.5">
                    <Button size="sm" variant="secondary" onClick={() => edit(h.id)} disabled={busy !== null}
                      data-testid={`heading-edit-${h.id}`}>Edit</Button>
                    <Button size="sm" variant="secondary" onClick={() => onDefault(h)} disabled={busy !== null}
                      data-testid={`heading-make-default-${h.id}`}>
                      {h.is_default ? 'Clear default' : 'Make default'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => onDelete(h)} disabled={busy !== null}
                      data-testid={`heading-delete-${h.id}`}>Delete</Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {editing && (
          <div className="space-y-4 pt-1 min-w-0" data-testid="heading-editor">
            {current && (
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => pickFile(current.id)}
                  data-testid="heading-import-into">
                  Update from an ESX
                </Button>
                {current.logo && (
                  <img src={current.logo} alt="Company logo" className="h-10 w-auto rounded-[4px]" data-testid="heading-logo" />
                )}
                {current.source_file && (
                  <span className="text-[11.5px] text-[var(--tx-faint)]">From {current.source_file}</span>
                )}
              </div>
            )}
            {text('name', 'Heading name (how you choose it on an estimate)')}
            <div className="grid grid-cols-[1fr_140px] gap-3 max-md:grid-cols-1">
              {text('company_name', 'Company name')}
              {text('company_code', 'Code')}
            </div>
            {text('header_info', 'Address and phone (as printed under the name)', 3)}
            {text('opening_statement', 'Opening statement', 6)}
            {text('closing_statement', 'Closing statement', 6)}
            <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
              {ESTIMATOR_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <FieldLabel htmlFor={`heading-est-${k}`}>{label}</FieldLabel>
                  <Input id={`heading-est-${k}`} value={estValue(k)} data-testid={`heading-est-${k}`}
                    onChange={(e) => setEstDraft((d) => ({ ...d, [k]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div>
              <FieldLabel htmlFor="heading-own-style">Heading text style (section headings in the ESX)</FieldLabel>
              <HeadingStyleControls idPrefix="heading-own-style" inheritLabel="Use my account style"
                value={styleDraft !== undefined ? styleDraft : current?.heading_style ?? null}
                onChange={setStyleDraft} />
            </div>
          </div>
        )}
        {flash && (
          <p className={`m-0 text-[12.5px] lg:col-span-2 ${flash.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}
            data-testid="heading-flash">
            {flash.msg}
          </p>
        )}
      </div>
    </Frame>
  );
}
